import React, { ReactElement, useCallback } from 'react';
import { HeadTag } from 'shared/components';
import { Box, Stack } from '@mui/material';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { Formik } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'shared/components/PageHeader';
import ScrollToError from 'shared/components/ScrollToError';
import { MCPServersSchema } from 'validators/MCPServersSchema';
import BasicIdentification from './components/BasicIdentification';
import MCPSpecificDetails from './components/MCPSpecificDetails';
import CapabilityAndFunctionsProfile from './components/CapabilityAndFunctionsProfile';
import IdentityAuthenticationAndAccessControl from './components/IdentityAuthenticationAndAccessControl';
import { MCPServersService } from 'services/MCPServersService';
import { Toast, Utils } from 'shared/helpers';

const FormMCPServers = ({ purpose = 'new' }): ReactElement => {
  const navigation = useNavigate();
  const { mcpId } = useParams();
  const location = useLocation();
  const serverDetail = location.state;

  const handleFormServers = useCallback(
    (values: any, formik: any) => {
      const request =
        purpose === 'edit'
          ? MCPServersService.updateMCPServer(mcpId, values)
          : MCPServersService.createMCPServers(values);

      request
        .then(() => {
          Toast.showSuccessMessage(
            `Successfully ${purpose === 'edit' ? 'updated' : 'created'} server`
          );
          navigation('/mcp-servers');
        })
        .catch((e) => {
          Utils.handleErrorResponse(e);
          formik.setSubmitting(false);
        });
    },
    [navigation]
  );

  return (
    <>
      <PageHeader
        title={purpose !== 'new' ? 'Edit MCP Server' : 'Register MCP Server'}
      />
      <Box className="ai-inventory form-datasets">
        <HeadTag
          title={purpose !== 'new' ? 'Edit MCP Server' : 'Register MCP Server'}
        />
        <Formik
          initialValues={
            purpose !== 'new'
              ? {
                  server_name: serverDetail.server_name,
                  business_owner: serverDetail.business_owner,
                  technical_owner: serverDetail.technical_owner,
                  team_or_function: serverDetail.team_or_function,
                  environment_scope: serverDetail.environment_scope,
                  hosting_model: serverDetail.hosting_model,
                  status: serverDetail.status,
                  short_description: serverDetail.short_description,
                  primary_use_cases: serverDetail.primary_use_cases,
                  mcp_server_type: serverDetail.mcp_server_type,
                  protocol_version: serverDetail.protocol_version,
                  transport_methods: serverDetail.transport_methods,
                  server_endpoint: serverDetail.server_endpoint,
                  tool_inventory_summary: serverDetail.tool_inventory_summary,
                  resource_inventory_summary:
                    serverDetail.resource_inventory_summary,
                  prompt_template_capabilities:
                    serverDetail.prompt_template_capabilities,
                  supports_multi_tenant: serverDetail.supports_multi_tenant,
                  client_compatibility_notes:
                    serverDetail.client_compatibility_notes,
                  version: serverDetail.version,
                  release_channel: serverDetail.release_channel,
                  change_log_link: serverDetail.change_log_link,
                  business_tasks_enabled: serverDetail.business_tasks_enabled,
                  connected_enterprise_systems:
                    serverDetail.connected_enterprise_systems,
                  data_sources_accessed: serverDetail.data_sources_accessed,
                  actions_supported: serverDetail.actions_supported,
                  can_trigger_side_effects:
                    serverDetail.can_trigger_side_effects,
                  supports_agentic_workflows:
                    serverDetail.supports_agentic_workflows,
                  auth_method: serverDetail.auth_method,
                  identity_provider: serverDetail.identity_provider,
                  authorization_model: serverDetail.authorization_model,
                  required_roles_permissions:
                    serverDetail.required_roles_permissions,
                  least_privilege_available:
                    serverDetail.least_privilege_available,
                  human_vs_machine_access: serverDetail.human_vs_machine_access,
                  secrets_management_approach:
                    serverDetail.secrets_management_approach,
                  token_lifetime_rotation: serverDetail.token_lifetime_rotation,
                  tenant_isolation_support:
                    serverDetail.tenant_isolation_support,
                  break_glass_details: serverDetail.break_glass_details,
                }
              : {
                  server_name: '',
                  business_owner: '',
                  technical_owner: '',
                  team_or_function: '',
                  environment_scope: 'dev',
                  hosting_model: 'vendor-hosted',
                  status: 'proposed',
                  short_description: '',
                  primary_use_cases: '',
                  mcp_server_type: '',
                  protocol_version: '',
                  transport_methods: [],
                  server_endpoint: '',
                  tool_inventory_summary: '',
                  resource_inventory_summary: '',
                  prompt_template_capabilities: '',
                  supports_multi_tenant: false,
                  client_compatibility_notes: '',
                  version: '',
                  release_channel: '',
                  change_log_link: '',
                  business_tasks_enabled: '',
                  connected_enterprise_systems: '',
                  data_sources_accessed: '',
                  actions_supported: [],
                  can_trigger_side_effects: false,
                  supports_agentic_workflows: false,
                  auth_method: 'OAuth',
                  identity_provider: '',
                  authorization_model: 'RBAC',
                  required_roles_permissions: '',
                  least_privilege_available: false,
                  human_vs_machine_access: 'Human',
                  secrets_management_approach: '',
                  token_lifetime_rotation: '',
                  tenant_isolation_support: false,
                  break_glass_details: '',
                }
          }
          validateOnMount
          enableReinitialize
          validationSchema={MCPServersSchema}
          onSubmit={(formValues, formik) => {
            formik.setSubmitting(true);
            handleFormServers(formValues, formik);
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <Box className="form-dataset-wrapper">
              <ScrollToError />
              <BasicIdentification purpose={undefined} />
              <MCPSpecificDetails purpose={undefined} />
              <CapabilityAndFunctionsProfile purpose={undefined} />
              <IdentityAuthenticationAndAccessControl purpose={undefined} />
              <Stack direction="row-reverse">
                <AppPrimaryButton
                  title={
                    purpose !== 'new' ? 'Update Server' : 'Register Server'
                  }
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  loadingText={purpose !== 'new' ? 'Updating' : 'Registering'}
                />
                <AppPrimaryButtonOutlined
                  title="Cancel"
                  onClick={() => {
                    navigation(-1);
                  }}
                  disabled={isSubmitting}
                />
              </Stack>
            </Box>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default FormMCPServers;
